import React from 'react'

import { getNodeScope, isNodeElement } from 'hsds/utils/dom'

export const FOCUSABLE_SELECTOR =
  'a[href],frame,iframe,input:not([type=hidden]):not([disabled]),select,textarea,button:not([disabled]):not([tabindex="-1"]),*[tabindex]:not([tabindex="-1"])'

export const findFocusableNodes = (
  nodeScope?: Node | null,
  customFocusableSelector?: string
) => {
  const scope = getNodeScope(nodeScope)
  return scope.querySelectorAll(customFocusableSelector || FOCUSABLE_SELECTOR)
}

export const findFirstFocusableNode = (
  nodeScope?: Node | null,
  customFocusableSelector?: string
) => {
  const focusableNodes = findFocusableNodes(nodeScope, customFocusableSelector)
  return focusableNodes[0]
}

export const findLastFocusableNode = (
  nodeScope?: Node | null,
  customFocusableSelector?: string
) => {
  const focusableNodes = findFocusableNodes(nodeScope, customFocusableSelector)
  return focusableNodes[focusableNodes.length - 1]
}

export const findCurrentFocusedNodeIndex = (
  currentNode?: unknown,
  nodeScope?: Node | null,
  customFocusableSelector?: string
): number | undefined | false => {
  if (!isNodeElement(currentNode)) return
  const focusableNodes = findFocusableNodes(nodeScope, customFocusableSelector)
  const currentNodeIndex = Array.prototype.indexOf.call(
    focusableNodes,
    currentNode
  )
  return currentNodeIndex !== -1 ? currentNodeIndex : false
}

export const findNextFocusableNode = (
  currentNode?: unknown,
  nodeScope?: Node | null,
  customFocusableSelector?: string
) => {
  if (!isNodeElement(currentNode)) return

  const scope = getNodeScope(nodeScope)
  const focusableNodes = findFocusableNodes(scope, customFocusableSelector)
  const currentNodeIndex =
    findCurrentFocusedNodeIndex(currentNode, scope, customFocusableSelector) ||
    0
  const nextNode =
    currentNodeIndex + 1 <= focusableNodes.length - 1
      ? focusableNodes[currentNodeIndex + 1]
      : scope

  return nextNode
}

export const findPreviousFocusableNode = (
  currentNode?: unknown,
  nodeScope?: Node | null,
  customFocusableSelector?: string
) => {
  if (!isNodeElement(currentNode)) return

  const scope = getNodeScope(nodeScope)
  const focusableNodes = findFocusableNodes(scope, customFocusableSelector)
  const currentNodeIndex =
    findCurrentFocusedNodeIndex(currentNode, scope, customFocusableSelector) ||
    0
  const prevNode =
    currentNodeIndex - 1 >= 0 ? focusableNodes[currentNodeIndex - 1] : scope

  return prevNode
}

export const focusNextFocusableNode = (
  currentNode?: unknown,
  nodeScope?: Node | null,
  customFocusableSelector?: string
) => {
  const scope = getNodeScope(nodeScope)
  const node = findNextFocusableNode(
    currentNode,
    scope,
    customFocusableSelector
  )
  node && (node as unknown as Document) !== document
    ? (node as HTMLElement).focus()
    : (
        findFirstFocusableNode(
          nodeScope,
          customFocusableSelector
        ) as HTMLElement
      ).focus()
  return node
}

export const focusPreviousFocusableNode = (
  currentNode?: unknown,
  nodeScope?: Node | null,
  customFocusableSelector?: string
) => {
  const scope = getNodeScope(nodeScope)
  const node = findPreviousFocusableNode(
    currentNode,
    scope,
    customFocusableSelector
  )
  node && (node as unknown as Document) !== document
    ? (node as HTMLElement).focus()
    : (
        findLastFocusableNode(nodeScope, customFocusableSelector) as HTMLElement
      ).focus()
  return node
}

/**
 * Method that will find the closest parent that can is focusable
 */
export function getClosestFocusableParent(
  element: HTMLElement,
  customFocusableSelector?: string
) {
  return (
    element.closest(customFocusableSelector || FOCUSABLE_SELECTOR) ||
    document.body
  )
}

const FOCUS_FALLBACK_SELECTORS = [
  '[aria-invalid="true"]',
  'input:checked',
  FOCUSABLE_SELECTOR,
]

function takesFocus(element: HTMLElement, options?: FocusOptions) {
  element.focus(options)
  return element === document.activeElement
}

/**
 * Focuses the first of `elements` that takes focus. An element that does not,
 * such as a `role="group"` wrapper, is tried through its invalid, then
 * checked, then other focusable descendants. Returns the focused element.
 */
export function focusFirstAvailable(
  elements: HTMLElement[],
  options?: FocusOptions
): HTMLElement | undefined {
  for (const element of elements) {
    if (takesFocus(element, options)) return element

    for (const selector of FOCUS_FALLBACK_SELECTORS) {
      const descendant = Array.from(
        element.querySelectorAll<HTMLElement>(selector)
      ).find(node => takesFocus(node, options))
      if (descendant) return descendant
    }
  }
  return undefined
}

/**
 * Method that will ensure tabbing will cycle between focusable nodes inside a given container
 */
export function manageTrappedFocus(
  container: HTMLElement,
  e: KeyboardEvent | React.KeyboardEvent,
  customFocusableSelector?: string
) {
  const focusedNode = e.target
  const isContainerFocused = focusedNode === container

  const focusableNodes = findFocusableNodes(container, customFocusableSelector)

  if (focusableNodes != null) {
    const focusedNodeIndex = Array.prototype.indexOf.call(
      focusableNodes,
      focusedNode
    )
    const isFirstNode = focusedNodeIndex === 0
    const isLastNode = focusedNodeIndex === focusableNodes.length - 1

    if (!e.shiftKey && isLastNode) {
      e.preventDefault()
      if (focusableNodes.length) {
        ;(focusableNodes[0] as HTMLElement).focus()
      }
    } else if (e.shiftKey && (isFirstNode || isContainerFocused)) {
      e.preventDefault()
      ;(focusableNodes[focusableNodes.length - 1] as HTMLElement).focus()
    }
  }
}
